import { useNavigate, useParams } from 'react-router-dom'
import { Table, Button, Tag } from '@bitcine/cinesend-theme/dist'
import Page from '../../components/layouts/page'
import { useState } from 'react'
import NewIssue from './components/new_issue'
import useTableFilters from '../../hooks/use_table_filters'
import ResolveIssueModal from './components/resolve_issue_modal'
import { useQuery } from '@tanstack/react-query'

export default function Issues() {
  const navigate = useNavigate()
  const airlineID = useParams().airlineID
  const cycleID = useParams().cycleID

  const [issue, setIssue] = useState(null)
  const { data: airline } = useQuery([`airlines/${airlineID}`])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const queryKey = `issues?cycleID=${cycleID}`
  const filteringOptions = [
    {
      label: 'Type',
      key: 'type',
      type: 'checkboxes',
      options: [
        {
          label: 'Metadata',
          key: 'metadata',
          value: 'metadata',
        },
        {
          label: 'Images',
          key: 'images',
          value: 'images',
        },
        {
          label: 'Content',
          key: 'content',
          value: 'content',
        },
      ],
    },
    {
      label: 'Status',
      key: 'status',
      type: 'checkboxes',
      options: [
        {
          label: 'Open',
          key: 'open',
          value: 'open',
        },
        {
          label: 'Resolved',
          key: 'resolved',
          value: 'resolved',
        },
      ],
    },
  ]
  const sortingOptions = [
    { key: 'severity', label: 'Severity', direction: 'desc' },
    { key: 'description', label: 'Description', direction: 'desc' },
    { key: 'asset', label: 'Asset', direction: 'desc' },
    { key: 'created_at', label: 'Created At', direction: 'desc' },
    { key: 'created_by', label: 'Created By', direction: 'desc' },
  ]
  const { searching, sorting, filtering, pagination, queryString } = useTableFilters({
    queryKey,
    filteringOptions,
    sortingOptions,
    enableSearch: true,
    queryExists: true,
    persistKey: `issues-table-${cycleID}`,
  })

  const { data, isLoading, isError, error: errors } = useQuery([queryKey, queryString])
  const issues = data?.data || []

  const goToAsset = (assetID, assetRouteValue, fieldValueID = null, langCode = null) => {
    // If no langCode is provided, try to get the default from the airline, otherwise fallback to ENG
    if (!langCode) {
      langCode = airline?.languages?.find((lang) => lang.id === airline.default_language_id)?.ife_code || 'ENG'
    }
    let root = `/airlines/${airlineID}/cycles/${cycleID}/metadata/${assetRouteValue}/${assetID}/details/${langCode}`
    if (fieldValueID) {
      root += `#field-errors-${fieldValueID}`
    }
    navigate(root)
  }

  const getSeverityTag = (severity) => {
    let tagType = 'success'
    switch (severity) {
      case 'resolved':
        tagType = 'success'
        break
      case 'warning':
        tagType = 'warning'
        break
      default:
        tagType = 'error'
        break
    }

    return <Tag label={severity.toUpperCase()} type={tagType} outline={true} />
  }

  const resolveCustomIssue = (data) => {
    setIssue(data)
    setIsModalOpen(true)
  }

  return (
    <Page
      title='Issues'
      buttons={[<NewIssue key={'new-issue'} type={'metadata'} cycleID={cycleID} queryKey={queryKey} />]}
    >
      <Table
        status={{
          pending: isLoading,
          error: errors,
          errorMessage: errors?.message,
        }}
        draggable={false}
        widths={[100, 400, 200, 200, 120, 100, 85]}
        header={{
          columns: [
            {
              text: 'Severity',
              key: 'severity',
            },
            {
              text: 'Description',
              key: 'description',
            },
            {
              text: 'Asset',
              key: 'asset',
            },
            {
              text: 'Created At',
              key: 'created_at',
            },
            {
              text: 'Created By',
              key: 'created_by',
            },
            {
              text: 'Type',
              key: 'type',
            },
            {
              text: 'Actions',
            },
          ],
          searching,
          sorting,
          filtering,
        }}
        body={{
          data: issues,
          row: {
            compact: true,
            spaced: true,
            truncate: false,
            disabled: (data, index) => data?.deleted_at,
            render: [
              (data) => getSeverityTag(data.deleted_at ? 'resolved' : data.severity),
              (data) => <div className='overflow-hidden'>{data.description}</div>,
              (data) => (
                <div
                  className='cursor-pointer hover:underline'
                  onClick={() => goToAsset(data.asset_id, data.asset_route_value)}
                >
                  {data?.asset_title}
                </div>
              ),
              (data) => data.created_at,
              (data) => data.created_by ?? 'System',
              (data) => <Tag label={`${data.type.toUpperCase()}`} type='neutral' outline={true} size='small' />,
              (data) => (
                <Button
                  onClick={() =>
                    data.asset_id
                      ? goToAsset(data.asset_id, data.asset_route_value, data.field_value_id, data.field_value_language)
                      : resolveCustomIssue(data)
                  }
                  size='small'
                  type='neutral'
                  tertiary={true}
                >
                  {data.deleted_at ? 'View' : 'Resolve'}
                </Button>
              ),
            ],
          },
          empty: {
            title: 'No issues found!',
            text: 'We could not find any issues at the moment',
            icon: 'check',
          },
        }}
        paginate={{ ...pagination, totalRows: data?.meta.total || 0 }}
      />
      {isModalOpen && <ResolveIssueModal issue={issue} onClose={() => setIsModalOpen(false)} queryKey={queryKey} />}
    </Page>
  )
}
